/**
 * Друкована форма інвентаризації НЗВ (ч.705) — аналогія inventory_counts.
 *
 * Два режими за станом документа:
 * - draft/in_progress — «Інвентаризаційний опис НЗВ»: перелік позицій з
 *   ПОРОЖНЬОЮ колонкою «Фактично» (комісія вписує від руки і підписує).
 * - posted — «Акт інвентаризації НЗВ»: фактичні кількості, собівартість,
 *   сума; snapshot аналітичний (без бухпроводок — рішення 5-A).
 * Підписи: голова та члени комісії, МВО (керівник підрозділу/цеху).
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { wipInventory } from '@/api/wipInventory';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { departmentsApi } from '@/api/hr';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmtQty = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { maximumFractionDigits: 3 });
const fmtMoney = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function WipInventoryPrintPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['wip-inventory', id],
    queryFn: () => wipInventory.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const whQ = useQuery({
    queryKey: ['warehouses', d?.organization_id],
    queryFn: () => refs.warehouses(d!.organization_id),
    enabled: !!d?.organization_id,
  });
  const depsQ = useQuery({
    queryKey: ['departments', d?.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: d!.organization_id, limit: 500 }),
    enabled: !!d?.organization_id,
  });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  }

  const isAct = d.status === 'posted';
  const whName = (whQ.data ?? []).find((w) => w.id === d.warehouse_id)?.name;
  const depName = (depsQ.data?.items ?? []).find((x) => x.id === d.department_id)?.name;
  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  const uomOf = (it: { nomenclature_id: string; uom_id?: string | null }) => {
    if (it.uom_id) return uomById.get(it.uom_id) ?? 'шт';
    const n = nomById.get(it.nomenclature_id);
    return (n?.base_uom_id && uomById.get(n.base_uom_id)) || 'шт';
  };
  const totFact = d.items.reduce((s, it) => s + Number(it.quantity_factual ?? 0), 0);
  const totCost = d.items.reduce((s, it) => s + Number(it.total_cost ?? 0), 0);

  return (
    <div className="print-page" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .wip-table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; font-size: 9pt; line-height: 1.15; }
        .wip-table th, .wip-table td { border: 1px solid #000; padding: 1px 4px; }
        .wip-table th { text-align: center; font-weight: 600; font-size: 8pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.wip_inventory',
        documentId: d.id,
        filename: `Інвентаризація НЗВ ${d.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '10pt' }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0 0.25rem' }}>
        {isAct
          ? 'Акт інвентаризації незавершеного виробництва'
          : 'Інвентаризаційний опис незавершеного виробництва'} № {d.document_number}
      </h2>
      <div style={{ textAlign: 'center', marginBottom: '0.75rem', fontSize: '10pt', color: '#555' }}>
        станом на {new Date(d.inventory_date).toLocaleDateString('uk-UA')}
        {depName && <>{' · '}підрозділ: <strong>{depName}</strong></>}
        {whName && <>{' · '}склад: <strong>{whName}</strong></>}
      </div>

      <table className="wip-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Найменування (сировина/напівфабрикат у переробці)</th>
            <th style={{ width: 45 }}>Од.</th>
            <th style={{ width: 90 }}>Фактично</th>
            {isAct && <th style={{ width: 100 }}>Собівартість од.</th>}
            {isAct && <th style={{ width: 100 }}>Сума</th>}
          </tr>
        </thead>
        <tbody>
          {d.items.map((it, i) => {
            const nom = nomById.get(it.nomenclature_id);
            return (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nom?.name ?? '—'}{it.comment && <span style={{ color: '#555' }}> — {it.comment}</span>}</td>
                <td style={{ textAlign: 'center' }}>{uomOf(it)}</td>
                <td style={{ textAlign: 'right' }}>
                  {isAct ? fmtQty(it.quantity_factual ?? 0) : ''}
                </td>
                {isAct && (
                  <td style={{ textAlign: 'right' }}>
                    {it.cost_per_unit != null ? fmtMoney(it.cost_per_unit) : '—'}
                  </td>
                )}
                {isAct && (
                  <td style={{ textAlign: 'right' }}>
                    {it.total_cost != null ? fmtMoney(it.total_cost) : '—'}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 700 }}>
            <td colSpan={3} style={{ textAlign: 'right' }}>Разом:</td>
            <td style={{ textAlign: 'right' }}>{isAct ? fmtQty(totFact) : ''}</td>
            {isAct && <td></td>}
            {isAct && <td style={{ textAlign: 'right' }}>{fmtMoney(totCost)}</td>}
          </tr>
        </tfoot>
      </table>

      {isAct ? (
        <p style={{ margin: '0.5rem 0' }}>
          Всього позицій {d.items.length}. Фактичну наявність незавершеного
          виробництва зафіксовано комісією станом на дату інвентаризації
          (аналітичний акт — бухгалтерські проводки не формуються).
        </p>
      ) : (
        <p style={{ margin: '0.5rem 0' }}>
          Всього позицій {d.items.length}. Комісія перевіряє фактичну
          наявність незавершеного виробництва у цеху, вписує кількості у
          колонку «Фактично» та підписує опис.
        </p>
      )}
      {d.comment && <p style={{ margin: '0.5rem 0', color: '#333' }}>Коментар: {d.comment}</p>}

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Голова комісії</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
              <br /><br />
              <strong>Члени комісії</strong><br /><br />
              _______________________<br /><br />
              _______________________
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Матеріально відповідальна особа</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б. (керівник підрозділу/цеху)</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
